import React, { useState, useEffect, useCallback, useRef } from 'react';
import confetti from 'canvas-confetti';

const ITEM_EMOJIS = {
  chicken: '🍗', beef: '🥩', steak: '🥩', pork: '🥩', meat: '🥩', bacon: '🥓',
  sausage: '🌭', sausages: '🌭', ham: '🍖',
  fish: '🐟', salmon: '🐟', tuna: '🐟', prawns: '🦐', shrimp: '🦐',
  egg: '🥚', eggs: '🥚',
  milk: '🥛', cheese: '🧀', butter: '🧈', yoghurt: '🥛', yogurt: '🥛', cream: '🥛',
  bread: '🍞', toast: '🍞', bagel: '🥯', croissant: '🥐', roll: '🍞', rolls: '🍞',
  rice: '🍚', pasta: '🍝', noodles: '🍜', spaghetti: '🍝',
  pizza: '🍕', burger: '🍔', taco: '🌮', sandwich: '🥪',
  apple: '🍎', apples: '🍎', banana: '🍌', bananas: '🍌',
  orange: '🍊', oranges: '🍊', lemon: '🍋', lemons: '🍋', lime: '🍋',
  grape: '🍇', grapes: '🍇', strawberry: '🍓', strawberries: '🍓',
  blueberry: '🫐', blueberries: '🫐', cherry: '🍒', cherries: '🍒',
  peach: '🍑', pear: '🍐', mango: '🥭', watermelon: '🍉', melon: '🍈',
  pineapple: '🍍', coconut: '🥥', avocado: '🥑', kiwi: '🥝',
  tomato: '🍅', tomatoes: '🍅', potato: '🥔', potatoes: '🥔',
  carrot: '🥕', carrots: '🥕', corn: '🌽', sweetcorn: '🌽',
  broccoli: '🥦', lettuce: '🥬', salad: '🥗', cucumber: '🥒',
  pepper: '🌶️', peppers: '🫑', chilli: '🌶️', onion: '🧅', onions: '🧅',
  garlic: '🧄', mushroom: '🍄', mushrooms: '🍄', beans: '🫘',
  cake: '🎂', cookie: '🍪', cookies: '🍪', biscuits: '🍪', biscuit: '🍪',
  chocolate: '🍫', candy: '🍬', sweets: '🍬', 'ice cream': '🍦', icecream: '🍦',
  donut: '🍩', doughnut: '🍩', pie: '🥧', cupcake: '🧁',
  coffee: '☕', tea: '🍵', juice: '🧃', water: '💧', wine: '🍷',
  beer: '🍺', cocktail: '🍹', whisky: '🥃', whiskey: '🥃',
  popcorn: '🍿', pretzel: '🥨', honey: '🍯', salt: '🧂',
  cereal: '🥣', pancake: '🥞', pancakes: '🥞', waffle: '🧇',
  nappies: '👶', nappy: '👶', baby: '👶',
  soap: '🧼', toothpaste: '🪥', shampoo: '🧴', toilet: '🧻',
  flowers: '💐', candle: '🕯️', candles: '🕯️',
};

function getEmojiForItem(text) {
  const lower = text.toLowerCase().trim();
  // Direct match
  if (ITEM_EMOJIS[lower]) return ITEM_EMOJIS[lower];
  // Check if any key is contained in the text
  for (const [key, emoji] of Object.entries(ITEM_EMOJIS)) {
    if (lower.includes(key)) return emoji;
  }
  return null;
}

function fireItemConfetti(emoji) {
  const scalar = 2;
  const shape = confetti.shapeFromText({ text: emoji, scalar });

  const defaults = {
    spread: 60,
    ticks: 60,
    gravity: 1.2,
    decay: 0.94,
    startVelocity: 20,
    shapes: [shape],
    scalar,
    flat: true,
  };

  confetti({ ...defaults, particleCount: 15, origin: { x: 0.3, y: 0.7 } });
  confetti({ ...defaults, particleCount: 15, origin: { x: 0.7, y: 0.7 } });
}

function fireDefaultConfetti() {
  confetti({
    particleCount: 30,
    spread: 60,
    origin: { x: 0.5, y: 0.7 },
    colors: ['#a855f7', '#14b8a6', '#f97316', '#eab308'],
    ticks: 50,
    gravity: 1.2,
    decay: 0.94,
    startVelocity: 18,
  });
}

export default function ShoppingList({ items, onAdd, onRemove, fullPage }) {
  const [text, setText] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [toast, setToast] = useState(null); // { item, timeoutId }
  const toastRef = useRef(null);

  const commitRemoval = useCallback((id) => {
    onRemove(id);
    setToast(prev => {
      if (prev?.item.id === id) return null;
      return prev;
    });
  }, [onRemove]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastRef.current?.timeoutId) clearTimeout(toastRef.current.timeoutId);
    };
  }, []);

  // Keep ref in sync for cleanup
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setText('');
  };

  const handleTick = (id) => {
    const item = items.find(i => i.id === id);
    if (!item) return;

    setRemovingId(id);

    // Fire confetti with matching emoji or default
    const emoji = getEmojiForItem(item.text);
    if (emoji) {
      fireItemConfetti(emoji);
    } else {
      fireDefaultConfetti();
    }

    setTimeout(() => {
      // If there's an existing toast, commit that removal immediately
      if (toastRef.current) {
        clearTimeout(toastRef.current.timeoutId);
        commitRemoval(toastRef.current.item.id);
      }

      onRemove(id);
      setRemovingId(null);

      const timeoutId = setTimeout(() => {
        setToast(null);
      }, 7000);

      setToast({ item, timeoutId });
    }, 350);
  };

  const handleUndo = () => {
    if (!toast) return;
    clearTimeout(toast.timeoutId);
    onAdd(toast.item.text, toast.item);
    setToast(null);
  };

  const handleDismissToast = () => {
    if (!toast) return;
    clearTimeout(toast.timeoutId);
    setToast(null);
  };

  return (
    <div className={fullPage ? 'animate-fade-in-up' : 'bg-white rounded-2xl p-5 shadow-sm border border-purple-100 mb-8'}>
      {fullPage && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">🛒 Shopping List</h1>
          <p className="text-sm text-gray-500 mt-1">
            {items.length === 0 ? 'Nothing on the list yet' : `${items.length} item${items.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      )}
      {!fullPage && (
        <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
          🛒 Shopping List
          {items.length > 0 && (
            <span className="text-xs font-medium text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
          )}
        </h2>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an item..."
          className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors text-sm"
        />
        <button
          type="submit"
          className="px-4 py-2.5 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 text-white rounded-xl font-semibold transition-colors text-sm"
        >
          Add
        </button>
      </form>

      {items.length === 0 && !toast ? (
        <p className="text-sm text-gray-400 text-center py-3">No items yet — add something above!</p>
      ) : (
        <ul className="space-y-1">
          {items.map((item) => (
            <li
              key={item.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-all ${
                removingId === item.id ? 'opacity-0 translate-x-4 line-through text-gray-300' : ''
              }`}
              style={{ transition: 'opacity 0.35s, transform 0.35s' }}
            >
              <button
                onClick={() => handleTick(item.id)}
                className="w-5 h-5 rounded-md border-2 border-gray-300 hover:border-purple-400 flex-shrink-0 flex items-center justify-center transition-colors"
                aria-label={`Mark ${item.text} as done`}
              >
                {removingId === item.id && (
                  <svg className="w-3 h-3 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
              <span className="text-sm text-gray-700 flex-1">{item.text}</span>
              {item.addedBy && (
                <span className="text-xs text-gray-400 flex-shrink-0">{item.addedBy}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Undo toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up">
          <div className="flex items-center gap-3 bg-gray-800 text-white px-4 py-3 rounded-xl shadow-lg text-sm">
            <span>"{toast.item.text}" removed</span>
            <button
              onClick={handleUndo}
              className="font-semibold text-purple-300 hover:text-purple-200 transition-colors"
            >
              Undo
            </button>
            <button
              onClick={handleDismissToast}
              className="text-gray-400 hover:text-gray-300 transition-colors ml-1"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
